/**
 * World Map Manager
 * 
 * Manages world map data, continent information, and city placement
 */

import { apiClient } from '../utils/APIClient.js';

export class WorldMapManager {
    constructor(gameState) {
        this.gameState = gameState;
        this.apiClient = apiClient;
        
        // Current world data
        this.currentWorld = null;
        this.continents = [];
        this.regions = new Map(); // Cache of loaded regions
        this.cities = []; // Cities on the map
        
        // Map view state
        this.viewX = 0;
        this.viewY = 0;
        this.zoomLevel = 1; // 1 = full map, 2 = 2x zoom, etc.
        this.viewWidth = 100; // Visible tiles width
        this.viewHeight = 100; // Visible tiles height
        
        // City placement settings
        this.minCityDistance = 10; // Minimum tiles between cities
    }
    
    /**
     * Initialize world map
     */
    async initialize(worldId = 1) {
        try {
            // Load world data
            const worldData = await this.apiClient.getWorld(worldId);
            this.currentWorld = worldData.world;
            this.continents = worldData.continents || [];
            
            // Load player cities
            const cities = await this.apiClient.getCities();
            this.cities = cities.filter(city => city.world_id === worldId);
            
            return true;
        } catch (error) {
            console.error('Failed to initialize world map:', error);
            return false;
        }
    }
    
    /**
     * Load map regions for a viewport
     */
    async loadRegions(x, y, width, height) {
        if (!this.currentWorld) return [];
        
        try {
            const regions = await this.apiClient.getWorldRegions(
                this.currentWorld.id,
                x, y, width, height
            );
            
            // Cache regions
            regions.forEach(region => {
                const key = `${region.region_x}_${region.region_y}`;
                this.regions.set(key, region);
            });
            
            return regions;
        } catch (error) {
            console.error('Failed to load regions:', error);
            return [];
        }
    }
    
    /**
     * Check if a position is valid for city placement
     */
    canPlaceCity(x, y) {
        // Check if position is within world bounds
        if (!this.currentWorld) return false;
        if (x < 0 || y < 0 || x >= this.currentWorld.width || y >= this.currentWorld.height) {
            return false;
        }
        
        // Check minimum distance from other cities
        for (const city of this.cities) {
            const distance = this.calculateDistance(x, y, city.world_x, city.world_y);
            if (distance < this.minCityDistance) {
                return false;
            }
        }
        
        // Check terrain (must be on land, not ocean)
        const region = this.getRegion(x, y);
        if (!region || region.terrain_type === 'ocean') {
            return false;
        }
        
        return true;
    }
    
    /**
     * Get region at coordinates
     */
    getRegion(x, y) {
        // Regions are typically stored at lower resolution
        // For now, assume 1:1 mapping
        const key = `${x}_${y}`;
        return this.regions.get(key);
    }
    
    /**
     * Calculate distance between two points
     */
    calculateDistance(x1, y1, x2, y2) {
        const dx = x2 - x1;
        const dy = y2 - y1;
        return Math.sqrt(dx * dx + dy * dy);
    }
    
    /**
     * Calculate travel time between two cities
     */
    calculateTravelTime(fromX, fromY, toX, toY, unitSpeed = 1) {
        const distance = this.calculateDistance(fromX, fromY, toX, toY);
        
        // Base speed: 1 tile per hour
        // Apply terrain modifiers
        const fromRegion = this.getRegion(fromX, fromY);
        const toRegion = this.getRegion(toX, toY);
        
        let speedModifier = 1.0;
        if (fromRegion) {
            if (fromRegion.terrain_type === 'mountain') speedModifier *= 0.75;
            if (fromRegion.terrain_type === 'plains') speedModifier *= 1.25;
        }
        
        const effectiveSpeed = unitSpeed * speedModifier;
        const travelTime = distance / effectiveSpeed; // hours
        
        return {
            distance: Math.round(distance),
            travelTime: Math.round(travelTime * 100) / 100, // Round to 2 decimals
            travelTimeHours: Math.floor(travelTime),
            travelTimeMinutes: Math.round((travelTime % 1) * 60)
        };
    }
    
    /**
     * Find available city placement locations near a point
     */
    findAvailableLocations(centerX, centerY, radius = 20, maxResults = 10) {
        const locations = [];
        
        for (let x = centerX - radius; x <= centerX + radius; x++) {
            for (let y = centerY - radius; y <= centerY + radius; y++) {
                if (this.canPlaceCity(x, y)) {
                    const distance = this.calculateDistance(centerX, centerY, x, y);
                    if (distance <= radius) {
                        const region = this.getRegion(x, y);
                        locations.push({
                            x, y,
                            distance: Math.round(distance),
                            terrain: region?.terrain_type || 'plains',
                            resources: region?.resource_nodes || []
                        });
                    }
                }
            }
        }
        
        // Sort by distance
        locations.sort((a, b) => a.distance - b.distance);
        
        return locations.slice(0, maxResults);
    }
    
    /**
     * Get continent at coordinates
     */
    getContinent(x, y) {
        return this.continents.find(continent => {
            return x >= continent.start_x && x <= continent.end_x &&
                   y >= continent.start_y && y <= continent.end_y;
        });
    }
    
    /**
     * Update viewport
     */
    setViewport(x, y, width, height, zoom = 1) {
        this.viewX = x;
        this.viewY = y;
        this.viewWidth = width;
        this.viewHeight = height;
        this.zoomLevel = zoom;
    }
    
    /**
     * Get cities in viewport
     */
    getCitiesInViewport() {
        return this.cities.filter(city => {
            return city.world_x >= this.viewX &&
                   city.world_x < this.viewX + this.viewWidth &&
                   city.world_y >= this.viewY &&
                   city.world_y < this.viewY + this.viewHeight;
        });
    }
    
    /**
     * Add city to map
     */
    addCity(city) {
        this.cities.push(city);
    }
    
    /**
     * Remove city from map
     */
    removeCity(cityId) {
        this.cities = this.cities.filter(city => city.id !== cityId);
    }
}

